import { AppButton } from '@9b3kuq5phwuk4txw/kiban-react-ui'
import { Lightbulb, SearchIcon } from 'lucide-react'
import { AppExcelDownloadButton } from '@/components/AppExcelDownloadButton'
import { AppSavedSearchQueries } from '@/components/AppSavedSearchQueries'
import type { OpportunityStatus } from '@/lib'
import { opportunitySavedSearch } from '@/lib/saved-search'
import type { OpportunityListItem } from '../-hooks/useOpportunities'
import { SearchBox } from './SearchBox'

/**
 * TableActions コンポーネントの Props
 *
 * @param searchText - 検索テキスト
 * @param onSearchTextChange - 検索テキスト変更ハンドラ
 * @param opportunities - オポチュニティ一覧（検索候補用）
 * @param opportunityStatuses - ステータス一覧
 * @param onOpenSearchDialog - 詳細検索ダイアログを開く
 * @param canAddOpportunity - オポチュニティ追加可能か
 * @param selectedTab - 選択中のタブ
 * @param onAddOpportunity - オポチュニティ追加ハンドラ
 * @param filteredOpportunities - フィルタ済みオポチュニティ（Excel出力用）
 * @param excelColumns - Excelカラム定義
 * @param sortKey - ソートキー
 * @param sortDirection - ソート方向
 * @param sortColumns - ソート用カラム定義
 */
interface TableActionsProps {
  searchText: string
  onSearchTextChange: (value: string) => void
  opportunities: OpportunityListItem[]
  opportunityStatuses: OpportunityStatus[]
  onOpenSearchDialog: () => void
  canAddOpportunity: boolean
  selectedTab: string
  onAddOpportunity: () => void
  filteredOpportunities: OpportunityListItem[]
  excelColumns: { label: string; accessor: (row: OpportunityListItem) => unknown }[]
  sortKey: string | null
  sortDirection: 'asc' | 'desc'
  sortColumns: { key: string; accessor?: (row: OpportunityListItem) => unknown }[]
}

/**
 * TableActions - オポチュニティ一覧のテーブル操作セクション
 *
 * 検索、アクションボタン、Excelエクスポートを含むテーブル操作UIを提供します。
 */
export function TableActions({
  searchText,
  onSearchTextChange,
  opportunities,
  opportunityStatuses,
  onOpenSearchDialog,
  canAddOpportunity,
  selectedTab,
  onAddOpportunity,
  filteredOpportunities,
  excelColumns,
  sortKey,
  sortDirection,
  sortColumns,
}: TableActionsProps) {
  return (
    <>
      <div className='flex gap-2 items-end'>
        <div className='flex-1'>
          <SearchBox
            label='オポチュニティを検索'
            tooltipContent='管理ID、タイトル、オポ種別、ステータス、起票者名、SF案件名、概要、取引先名、取引先責任者名、売上計上グループ名、組織名などで検索できます'
            value={searchText}
            onChange={onSearchTextChange}
            opportunities={opportunities}
            opportunityStatuses={opportunityStatuses}
            placeholder='タイトル、案件名、取引先名などで絞り込み'
          />
        </div>
        <AppSavedSearchQueries config={opportunitySavedSearch} onSelect={onSearchTextChange} />
        <AppButton onClick={onOpenSearchDialog} variant='outline'>
          <SearchIcon />
          詳細検索
        </AppButton>
        {canAddOpportunity && (selectedTab === 'captain' || selectedTab === 'sales') && (
          <AppButton onClick={onAddOpportunity}>
            <Lightbulb />
            {selectedTab === 'captain' ? 'キャプテンオポ追加' : '営業オポ追加'}
          </AppButton>
        )}
      </div>

      <div className='flex justify-end'>
        <AppExcelDownloadButton
          data={filteredOpportunities}
          columns={excelColumns}
          filename='オポチュニティ一覧'
          isFiltered={searchText !== ''}
          sortKey={sortKey}
          sortDirection={sortDirection}
          sortColumns={sortColumns}
        />
      </div>
    </>
  )
}
